import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import {Alert, Box, IconButton, InputAdornment, MenuItem, Stack, Switch, TextField, Tooltip, Typography} from "@mui/material";
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker";
import Property from "crqlar-react-common/dist/components/Property";
import React from "react";
import {useTranslation} from "react-i18next";
import {formatMoney, formatPercent} from "../public/format.util";
import {TipBase, TipFormat, TipUnit} from "../public/digitalSigning.types";
import {parseAmountToCents} from "../public/tip.util";
import {SETTINGS_ROWS_GAP, SettingsSection, SettingsSectionDivider} from "./DigitalSigningSettingsSection";
import {TIP_ROUNDING_STEPS, TIP_SUGGESTIONS} from "./digitalSigningTemplate.model";
import {
    isPercentList,
    TemplateFormErrors,
    tipRoundingExample,
    TipSettingsForm,
    TipSuggestionList,
    tipSuggestionLists
} from "./digitalSigningTemplate.util";

interface Props {
    tip: TipSettingsForm
    errors: TemplateFormErrors
    onChange: (tip: TipSettingsForm) => void
    currency: string
    language: string
    preselectAllowed: boolean
    disabled?: boolean
}

const FORMATS: {value: TipFormat, label: string}[] = [
    {value: 'SMART', label: 'Smart'},
    {value: 'FIXED', label: 'Absolute'},
    {value: 'PERCENT', label: 'Percent'},
];

// Smart's units below the threshold and from it, as the Unit select offers them
const SMART_UNITS: {lower: TipUnit, upper: TipUnit, label: string}[] = [
    {lower: 'FIXED', upper: 'FIXED', label: 'Below threshold absolute, above threshold absolute'},
    {lower: 'FIXED', upper: 'PERCENT', label: 'Below threshold absolute, above threshold %'},
    {lower: 'PERCENT', upper: 'FIXED', label: 'Below threshold %, above threshold absolute'},
    {lower: 'PERCENT', upper: 'PERCENT', label: 'Below threshold %, above threshold %'},
];

// The inputs' names by list, as translation keys
const FIELD_LABELS: Record<TipSuggestionList, string> = {
    amounts: 'Amount {{number}}',
    upperAmounts: 'Amount {{number}} from the threshold',
    lowerPercents: 'Percentage {{number}} below the threshold',
    percents: 'Percentage {{number}}',
};

// The status picker's values are strings: none, or a suggestion's index
const NONE = 'none';

const suggestionGridSx = (columns: number) => ({
    display: 'grid',
    gridTemplateColumns: [...Array.from({length: columns}, () => 'minmax(0, 1fr)'), '40px'].join(' '),
    columnGap: 1.5,
    alignItems: 'start',
    width: '100%',
});

function SuggestionPicker({value, onChange, label, disabled}: {
    value: number | null
    onChange: (index: number | null) => void
    label: string
    disabled?: boolean
}) {
    const {t} = useTranslation();

    return (
        <NewStatusPicker<string>
            value={value === null ? NONE : String(value)}
            onChange={next => onChange(next === NONE ? null : Number(next))}
            ariaLabel={label}
            disabled={disabled}
            options={[
                {value: NONE, label: t('None')},
                ...Array.from({length: TIP_SUGGESTIONS}, (_, index) => ({
                    value: String(index),
                    label: t('Suggestion {{number}}', {number: index + 1}),
                })),
            ]}
        />
    );
}

function Note({children}: {children: React.ReactNode}) {
    return (
        <Property title={''}>
            <Alert severity={'info'} variant={'outlined'} sx={{flex: 1, minWidth: 0, alignItems: 'center', py: 0}}>
                {children}
            </Alert>
        </Property>
    );
}

function RoundingExample({tip, money, language}: {tip: TipSettingsForm, money: (cents: number) => string, language: string}) {
    const {t} = useTranslation();
    const example = tipRoundingExample(tip);
    const values = {
        percent: formatPercent(example.percent, language, 2),
        base: money(example.baseCents),
        exact: money(example.exactCents),
        step: money(example.roundingCents),
        amount: money(example.amountCents),
    };

    return (
        <Note>
            {example.roundingCents <= 1
                ? t('Example: {{percent}} of {{base}} is {{amount}} – the page offers it to the cent.', values)
                : t('Example: {{percent}} of {{base}} is {{exact}}; rounded to {{step}}, the page offers {{amount}}. A small tip is never rounded down to zero.', values)}
        </Note>
    );
}

/** Editor for template tip configuration (fixed amounts, percentages, and rounding). */
const DigitalSigningTipEditor = ({tip, errors, onChange, currency, language, preselectAllowed, disabled}: Props) => {
    const {t} = useTranslation();
    const lists = tipSuggestionLists(tip);
    const percents = lists.some(isPercentList);
    const smart = tip.format === 'SMART';
    const thresholdCents = parseAmountToCents(tip.threshold);
    // Staff read the currency's code, like in the folio dialog
    const money = (cents: number, trimWhole = false) => formatMoney(cents, currency, language, trimWhole, 'code');
    const threshold = thresholdCents ? money(thresholdCents, true) : undefined;
    const set = (patch: Partial<TipSettingsForm>) => onChange({...tip, ...patch});
    const setAt = (list: TipSuggestionList, index: number, value: string) => (
        set({[list]: tip[list].map((current, position) => (position === index ? value : current))})
    );

    const gridSx = suggestionGridSx(lists.length);
    // Smart's two columns are its tiers, whatever they are in
    const caption = (list: TipSuggestionList, column: number) => {
        if (!smart) return isPercentList(list) ? t('Percentage') : t('Absolute');
        if (column === 0) return threshold ? t('Below {{amount}}', {amount: threshold}) : t('Below the threshold');
        return threshold ? t('From {{amount}}', {amount: threshold}) : t('From the threshold');
    };

    const valueField = (list: TipSuggestionList, index: number) => {
        const error = errors[list][index];
        const percent = isPercentList(list);
        return (
            <TextField
                key={list}
                size={'small'}
                fullWidth
                value={tip[list][index]}
                onChange={event => setAt(list, index, event.target.value)}
                error={!!error}
                helperText={error && t(error)}
                disabled={disabled}
                inputProps={{inputMode: 'decimal', maxLength: percent ? 6 : 10, 'aria-label': t(FIELD_LABELS[list], {number: index + 1})}}
                InputProps={{endAdornment: <InputAdornment position={'end'}>{percent ? '%' : currency}</InputAdornment>}}
            />
        );
    };

    const suggestionRow = (index: number) => {
        const highlighted = tip.highlightedPresetIndex === index;
        return (
            <Box sx={gridSx}>
                {lists.map(list => valueField(list, index))}
                <Tooltip title={highlighted ? t('Highlighted on the page') : t('Highlight on the page')}>
                    <span>
                        <IconButton
                            aria-label={t('Highlight suggestion {{number}}', {number: index + 1})}
                            aria-pressed={highlighted}
                            disabled={disabled}
                            onClick={() => set({highlightedPresetIndex: highlighted ? null : index})}
                            sx={{width: 40, height: 40, color: highlighted ? 'warning.main' : 'action.active'}}
                        >
                            {highlighted ? <StarRoundedIcon/> : <StarBorderRoundedIcon/>}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        );
    };

    const units = SMART_UNITS.findIndex(option => option.lower === tip.smartLowerUnit && option.upper === tip.smartUpperUnit);
    // Smart compares the threshold with the base too, so it has one even without a % tier
    const baseTooltip = !smart
        ? t('The amount the percentages apply to.')
        : percents ? t('The amount the threshold and the percentages apply to.') : t('The amount the threshold applies to.');

    return (
        <>
            <SettingsSection title={t('Tip')}>
                <Stack gap={SETTINGS_ROWS_GAP}>
                    <Property title={t('Ask for a tip')} tooltip={t('Off: the page asks for no tip. On: the guest can always choose “No tip”.')}>
                        <Switch
                            checked={tip.enabled}
                            onChange={event => set({enabled: event.target.checked})}
                            disabled={disabled}
                            inputProps={{'aria-label': t('Ask for a tip')}}
                        />
                    </Property>

                    {tip.enabled && (
                        <>
                            <Property
                                title={t('Suggestions')}
                                tooltip={t('Smart: one set of suggestions below a threshold and another from it. The guest can always enter their own amount.')}
                            >
                                <NewStatusPicker<TipFormat>
                                    value={tip.format}
                                    onChange={format => set({format})}
                                    ariaLabel={t('Suggestions')}
                                    disabled={disabled}
                                    options={FORMATS.map(format => ({value: format.value, label: t(format.label)}))}
                                />
                            </Property>

                            {smart && (
                                <>
                                    <Property title={t('Unit')} tooltip={t('What the suggestions below and from the threshold are in.')}>
                                        <TextField
                                            select
                                            size={'small'}
                                            fullWidth
                                            value={units}
                                            onChange={event => {
                                                const {lower, upper} = SMART_UNITS[Number(event.target.value)];
                                                set({smartLowerUnit: lower, smartUpperUnit: upper});
                                            }}
                                            disabled={disabled}
                                            inputProps={{'aria-label': t('Unit')}}
                                        >
                                            {SMART_UNITS.map((option, index) => (
                                                <MenuItem key={option.label} value={index}>{t(option.label)}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Property>
                                    <Property title={t('Threshold')} tooltip={t('Bills below it get the suggestions on the left, bills from it those on the right.')}>
                                        <TextField
                                            size={'small'}
                                            fullWidth
                                            value={tip.threshold}
                                            onChange={event => set({threshold: event.target.value})}
                                            error={!!errors.threshold}
                                            helperText={errors.threshold && t(errors.threshold)}
                                            disabled={disabled}
                                            inputProps={{inputMode: 'decimal', maxLength: 10, 'aria-label': t('Threshold')}}
                                            InputProps={{endAdornment: <InputAdornment position={'end'}>{currency}</InputAdornment>}}
                                        />
                                    </Property>
                                </>
                            )}

                            {tip.format !== 'FIXED' && (
                                <Property title={t('Base')} tooltip={baseTooltip}>
                                    <TextField
                                        select
                                        size={'small'}
                                        fullWidth
                                        value={tip.base}
                                        onChange={event => set({base: event.target.value as TipBase})}
                                        disabled={disabled}
                                        inputProps={{'aria-label': t('Base')}}
                                    >
                                        <MenuItem value={'GROSS_AFTER_DISCOUNT'}>{t('Total incl. VAT after discounts')}</MenuItem>
                                        <MenuItem value={'NET_BEFORE_TAX'}>{t('Net before VAT')}</MenuItem>
                                    </TextField>
                                </Property>
                            )}

                            {percents && (
                                <>
                                    <Property title={t('Round percentages to')}>
                                        <TextField
                                            select
                                            size={'small'}
                                            fullWidth
                                            value={tip.roundingCents}
                                            onChange={event => set({roundingCents: Number(event.target.value)})}
                                            disabled={disabled}
                                            inputProps={{'aria-label': t('Round percentages to')}}
                                        >
                                            {TIP_ROUNDING_STEPS.map(step => (
                                                <MenuItem key={step} value={step}>{step === 1 ? t('Exact') : money(step)}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Property>
                                    <RoundingExample tip={tip} money={money} language={language}/>
                                </>
                            )}
                        </>
                    )}
                </Stack>
            </SettingsSection>

            {tip.enabled && (
                <>
                    <SettingsSectionDivider/>
                    <SettingsSection title={t('Suggestions')}>
                        <Stack gap={SETTINGS_ROWS_GAP}>
                            <Property title={''}>
                                <Box sx={{...gridSx, alignItems: 'end'}}>
                                    {lists.map((list, column) => (
                                        <Typography key={list} variant={'body2'} color={'text.secondary'}>{caption(list, column)}</Typography>
                                    ))}
                                    <Tooltip title={t('Outlined on the guest page as a recommendation, never selected for the guest.')}>
                                        <Box component={'span'} tabIndex={0} aria-label={t('Highlight')} sx={{display: 'flex', justifyContent: 'center', color: 'text.secondary'}}>
                                            <StarRoundedIcon sx={{fontSize: 16}}/>
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </Property>

                            {Array.from({length: TIP_SUGGESTIONS}, (_, index) => (
                                <Property key={index} title={t('Suggestion {{number}}', {number: index + 1})}>
                                    {suggestionRow(index)}
                                </Property>
                            ))}

                            {preselectAllowed && (
                                <Property title={t('Preselected tip')} tooltip={t('The guest can still choose another tip or none.')}>
                                    <SuggestionPicker
                                        value={tip.preselectedPresetIndex}
                                        onChange={preselectedPresetIndex => set({preselectedPresetIndex})}
                                        label={t('Preselected tip')}
                                        disabled={disabled}
                                    />
                                </Property>
                            )}
                        </Stack>
                    </SettingsSection>
                </>
            )}
        </>
    );
};

export default DigitalSigningTipEditor;
